import Pusher, { Channel } from 'pusher-js'
import { type IProviderService } from '../IProviderService'
import { Provider, type RealtimePluginOptions } from '../../RealtimePluginOptions'
import { type TEventName } from '../../TEventName'
import { type TEvent as RealtimeEvent } from '../../TEvent'
import { AesGcmDecryptor, type RealtimeDecryptor } from '../../crypto/AesGcmDecryptor'
import { parseDsn } from './parseDsn'

export class PusherService implements IProviderService {
  private pusher!: Pusher
  private channels: Map<string, Channel> = new Map()
  private handlers: Map<string, Map<TEventName, Map<unknown, (data: unknown) => void>>> = new Map()
  private tabUUID: string | null = null
  private decryptor: RealtimeDecryptor | null = null

  init(config: RealtimePluginOptions, tabUUID: string): void {
    if (config.provider !== Provider.PUSHER) {
      throw new Error('PusherService received non-pusher config.')
    }

    const { key, cluster } = parseDsn(config.dsn)
    this.pusher = new Pusher(key, {
      cluster,
    })
    this.tabUUID = tabUUID
    this.channels.clear()
    this.handlers.clear()
  }

  setKey(keyBase64: string | null): void {
    this.decryptor = keyBase64 ? new AesGcmDecryptor(keyBase64) : null
  }

  subscribe(channel: string): void {
    if (!this.channels.has(channel)) {
      this.channels.set(channel, this.pusher.subscribe(channel))
    }
  }

  unsubscribe(channel: string): void {
    if (!this.channels.has(channel)) return

    this.pusher.unsubscribe(channel)
    this.channels.delete(channel)
    this.handlers.delete(channel)
  }

  connect(): void {
    this.pusher.connect()
  }

  disconnect(): void {
    if (this.pusher) {
      this.pusher.disconnect()
    }
    this.channels.clear()
    this.handlers.clear()
  }

  onEvent<T>(channel: string, event: TEventName, handler: (data: T) => void): void {
    const boundChannel = this.channels.get(channel)
    if (!boundChannel) {
      throw new Error(`Channel "${channel}" not subscribed. Call "subscribe()" first.`)
    }

    const wrapped = async (raw: unknown) => {
      const payload = raw as RealtimeEvent<T>
      if (payload.tabUuid === this.tabUUID) {
        return
      }

      const data = this.decryptor ? await this.decryptor.decrypt(payload.data) : payload.data
      handler(data as T)
    }

    if (!this.handlers.has(channel)) {
      this.handlers.set(channel, new Map())
    }
    const byEvent = this.handlers.get(channel)!
    if (!byEvent.has(event)) {
      byEvent.set(event, new Map())
    }
    byEvent.get(event)!.set(handler, wrapped)

    boundChannel.bind(event, wrapped)
  }

  offEvent<T>(channel: string, event: TEventName, handler: (data: T) => void): void {
    const boundChannel = this.channels.get(channel)
    if (!boundChannel) return

    const byEvent = this.handlers.get(channel)
    const wrapped = byEvent?.get(event)?.get(handler)
    if (!wrapped) return

    boundChannel.unbind(event, wrapped)

    byEvent!.get(event)!.delete(handler)
    if (byEvent!.get(event)!.size === 0) {
      byEvent!.delete(event)
    }
    if (byEvent!.size === 0) {
      this.handlers.delete(channel)
    }
  }
}
